import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useNavigation } from '../../context/NavigationContext';
import { Card, StatusBadge, ScoreDisplay, KnockoutBracket, RoundRobinTable, Modal, Ico, Icons, ShareButton, PlayerAvatar, isPlayerRegistered, resolveTeam, TournamentListSkeleton, TournamentDetailSkeleton, TournamentCard } from '../../components/common';
import { tournamentAPI, notificationAPI } from '../../services/api';

export function TournamentsList() {
  const { savePageState, getPageState } = useNavigation();
  const [filter, setFilter] = useState(() => getPageState('filter', null));
  const [searchQuery, setSearchQuery] = useState(() => getPageState('searchQuery', ''));
  const [ready, setReady] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [page, setPage] = useState(() => getPageState('page', 1));
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ future: 0, ongoing: 0, upcoming: 0, completed: 0 });
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const limit = 9;

  // Save state on changes
  useEffect(() => {
    savePageState('filter', filter);
  }, [filter, savePageState]);

  useEffect(() => {
    savePageState('searchQuery', searchQuery);
  }, [searchQuery, savePageState]);

  useEffect(() => {
    savePageState('page', page);
  }, [page, savePageState]);

  const loadTournaments = async (pageNum = 1, filterStatus = 'future') => {
    setLoading(true);
    try {
      const statusFilter = filterStatus === 'future' ? '&status=upcoming,ongoing' : `&status=${filterStatus}`;
      const res = await tournamentAPI.getAll(`page=${pageNum}&limit=${limit}&sort=-startDate${statusFilter}`);
      
      if (pageNum === 1) {
        setTournaments(res.data || []);
      } else {
        setTournaments(prev => [...prev, ...(res.data || [])]);
      }
      
      setHasMore(res.data && res.data.length === limit);
      setReady(true);
    } catch (err) {
      console.error(err);
      setReady(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load stats and initial data in parallel
        const [statsRes, dataRes] = await Promise.all([
          tournamentAPI.getStats(),
          tournamentAPI.getAll('page=1&limit=9&sort=-startDate&status=upcoming,ongoing')
        ]);
        
        // Set stats
        setStats(statsRes.data);
        
        // Determine initial filter
        const initialFilter = statsRes.data.future > 0 ? 'future' : 'completed';
        setFilter(initialFilter);
        
        // Use data if it matches the initial filter
        if (initialFilter === 'future') {
          setTournaments(dataRes.data || []);
          setHasMore(dataRes.data && dataRes.data.length === limit);
          setReady(true);
        } else {
          // Load completed data
          const completedRes = await tournamentAPI.getAll(`page=1&limit=${limit}&sort=-startDate&status=completed`);
          setTournaments(completedRes.data || []);
          setHasMore(completedRes.data && completedRes.data.length === limit);
          setReady(true);
        }
        
        setInitialLoadDone(true);
      } catch (err) {
        console.error(err);
        setFilter('future');
        setReady(true);
        setInitialLoadDone(true);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    // Only load tournaments if filter changes after initial load AND filter is different from initial
    if (filter && initialLoadDone) {
      // Skip if we already have the data from initial load
      const isInitialData = (filter === 'future' && tournaments.length > 0);
      if (isInitialData) return;
      
      setPage(1);
      setReady(false);
      loadTournaments(1, filter);
    }
  }, [filter, initialLoadDone]);

  const handleShowMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadTournaments(nextPage, filter);
  };

  if (!ready) return <TournamentListSkeleton />;
  
  // Filter tournaments by search query
  const searchFiltered = tournaments.filter(t => {
    const searchLower = searchQuery.toLowerCase();
    return !searchLower || 
      t.name.toLowerCase().includes(searchLower) ||
      t.location?.toLowerCase().includes(searchLower) ||
      t.surface?.toLowerCase().includes(searchLower);
  });
  
  // Calculate filtered counts for each status
  const filteredStats = {
    future: searchFiltered.filter(t => t.status === 'upcoming' || t.status === 'ongoing').length,
    completed: searchFiltered.filter(t => t.status === 'completed').length,
    ongoing: searchFiltered.filter(t => t.status === 'ongoing').length,
    upcoming: searchFiltered.filter(t => t.status === 'upcoming').length
  };
  
  const filtered = searchFiltered;

  return (
    <div className="container">
      <h1 className="heading" style={{ marginBottom: 16 }}>TOURNAMENTS</h1>
      
      {/* Search Input */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          className="form-input"
          placeholder="Search tournaments by name, location, or surface..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
          onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
          onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
        />
      </div>
      
      <div className="filter-tabs">
        {[{ key: 'future', label: 'Future' }, { key: 'completed', label: 'Completed' }, { key: 'ongoing', label: 'Ongoing' }, { key: 'upcoming', label: 'Upcoming' }].filter(f => !(f.key === 'future' && stats.future === 0)).map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`filter-tab ${filter === f.key ? 'filter-tab-active' : ''}`}>
            {f.label}
            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: filter === f.key ? 'rgba(189,255,0,0.2)' : '#EEF1F6', color: filter === f.key ? '#BDFF00' : '#8892A6', padding: '2px 8px', borderRadius: 10 }}>
              {searchQuery ? filteredStats[f.key] : stats[f.key]}
            </span>
          </button>
        ))}
      </div>
      <div className="grid-cards">
        {filtered.map(t => <TournamentCard key={t.id} tournament={t} />)}
        {filtered.length === 0 && (
          <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#B0B8C9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 010-5H6"/>
                <path d="M18 9h1.5a2.5 2.5 0 000-5H18"/>
                <path d="M4 22h16"/>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/>
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/>
                <path d="M18 2H6v7a6 6 0 0012 0V2z"/>
              </svg>
            </div>
            <h3 className="empty-title">No Tournaments Found</h3>
            <p className="empty-sub">{searchQuery ? 'No tournaments match your search' : filter !== 'all' ? 'Try a different filter' : 'No tournaments available yet'}</p>
          </div>
        )}
      </div>
      {hasMore && filtered.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button 
            onClick={handleShowMore} 
            disabled={loading}
            style={{ padding: '12px 32px', background: loading ? '#B0B8C9' : '#00205B', color: loading ? '#fff' : '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontSize: 13, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5 }}
          >
            {loading ? 'Loading...' : 'Show More'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Public Group Stage View ──────────────────────────────────────────────────
function PublicGroupView({ groupStageData }) {
  const [activeGroup, setActiveGroup] = useState('all');
  const [activeTab, setActiveTab] = useState('standings');

  const groups = groupStageData.groups || [];

  if (groups.length === 0) return (
    <div style={{ textAlign:'center', padding:'40px 20px', color:'var(--gray-400)', fontSize:13 }}>
      No groups set up yet.
    </div>
  );

  // ── reusable standings table ──────────────────────────────────────────────────
  const StandingsTable = ({ gid }) => {
    const rows  = groupStageData.standingsMap?.[gid] || [];
    const teams = groupStageData.teamsMap?.[gid] || [];
    const fx    = groupStageData.fixturesMap?.[gid] || [];
    const played = fx.filter(f => f.matchId?.status === 'completed').length;
    const total  = fx.length;
    if (rows.length === 0) return (
      <p style={{ color:'var(--gray-400)', fontSize:13, textAlign:'center', padding:'16px 0' }}>No results yet.</p>
    );
    return (
      <div style={{ overflowX:'auto' }}>
        {total > 0 && (
          <div style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--gray-400)', marginBottom:4 }}>
              <span>Progress</span><span>{played}/{total} played</span>
            </div>
            <div className="bar"><div className="bar-fill" style={{ width:`${(played/total)*100}%`, background:'var(--navy)' }} /></div>
          </div>
        )}
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:'2px solid var(--gray-100)' }}>
              {['#','Team','P','W','L','Pts',''].map((h,i) => (
                <th key={i} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:.5, fontFamily:'var(--font-display)', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const team = teams.find(t => String(t._id) === String(row.participantId));
              const isQ  = team?.status === 'qualified';
              const isE  = team?.status === 'eliminated';
              return (
                <tr key={idx} style={{ borderBottom:'1px solid var(--gray-100)', background: isQ ? 'rgba(67,160,71,.05)' : 'transparent' }}>
                  <td style={{ padding:'9px 10px', fontWeight:800, color:'var(--navy)', fontFamily:'var(--font-display)', width:28 }}>{idx+1}</td>
                  <td style={{ padding:'9px 10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:28, height:28, borderRadius:6, background:'var(--navy)', color:'var(--lime)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, fontFamily:'var(--font-display)', flexShrink:0 }}>
                        {team?.teamCode || '?'}
                      </div>
                      <span style={{ fontWeight:600, color:'var(--navy)', fontSize:13 }}>{team?.teamName || row.participantId}</span>
                    </div>
                  </td>
                  <td style={{ padding:'9px 10px', color:'var(--gray-500)' }}>{row.matchesPlayed}</td>
                  <td style={{ padding:'9px 10px', color:'var(--green)', fontWeight:700 }}>{row.matchesWon}</td>
                  <td style={{ padding:'9px 10px', color:'var(--red)' }}>{row.matchesLost}</td>
                  <td style={{ padding:'9px 10px', fontWeight:800, color:'var(--navy)', fontFamily:'var(--font-display)' }}>{row.matchPoints}</td>
                  <td style={{ padding:'9px 10px' }}>
                    {isQ && <span style={{ fontSize:10, fontWeight:700, color:'#fff', background:'var(--green)', padding:'2px 8px', borderRadius:10 }}>✓ Q</span>}
                    {isE && <span style={{ fontSize:10, fontWeight:700, color:'var(--gray-400)', background:'var(--gray-100)', padding:'2px 8px', borderRadius:10 }}>Out</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── reusable fixtures list ────────────────────────────────────────────────────
  const FixturesList = ({ gid }) => {
    const fixtures = groupStageData.fixturesMap?.[gid] || [];
    if (fixtures.length === 0) return (
      <p style={{ color:'var(--gray-400)', fontSize:13, textAlign:'center', padding:'16px 0' }}>No matches scheduled yet.</p>
    );
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {fixtures.map((fix, idx) => {
          const m    = fix.matchId;
          const t1   = fix.team1Id;
          const t2   = fix.team2Id;
          const done = m?.status === 'completed';
          const live = m?.status === 'live';
          const sets = m?.score?.sets || [];
          return (
            <div key={fix._id} style={{ background:'var(--card-bg)', border:`1px solid ${live ? 'var(--red)' : done ? 'var(--gray-200)' : 'var(--card-border)'}`, borderRadius:10, overflow:'hidden' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 12px', background: live ? 'rgba(229,57,53,.05)' : 'var(--gray-50)', borderBottom:'1px solid var(--card-border)' }}>
                <span style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)' }}>M{idx+1}</span>
                <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:10, background: live ? 'var(--red)' : done ? 'var(--green)' : 'var(--gray-200)', color: live || done ? '#fff' : 'var(--gray-500)', textTransform:'uppercase', letterSpacing:.5 }}>
                  {live ? '● LIVE' : done ? 'Done' : 'Upcoming'}
                </span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px' }}>
                <div style={{ flex:1, display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                  <div style={{ width:28, height:28, borderRadius:6, background:'var(--navy)', color:'var(--lime)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, fontFamily:'var(--font-display)', flexShrink:0 }}>{t1?.teamCode||'?'}</div>
                  <span style={{ fontSize:13, fontWeight: String(t1?._id)===String(m?.winner)?700:500, color: String(t1?._id)===String(m?.winner)?'var(--navy)':'var(--gray-500)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t1?.teamName||'TBD'}</span>
                </div>
                <div style={{ display:'flex', gap:4, alignItems:'center', flexShrink:0 }}>
                  {done && sets.length > 0
                    ? sets.map((set, i) => {
                        const t1Won = set.p1 > set.p2;
                        return (
                          <React.Fragment key={i}>
                            <span style={{ fontSize:13, fontWeight:t1Won?700:400, color:t1Won?'var(--navy)':'var(--gray-400)', fontFamily:'var(--font-display)', minWidth:16, textAlign:'center' }}>{set.p1}</span>
                            <span style={{ fontSize:11, color:'var(--gray-200)' }}>–</span>
                            <span style={{ fontSize:13, fontWeight:!t1Won?700:400, color:!t1Won?'var(--navy)':'var(--gray-400)', fontFamily:'var(--font-display)', minWidth:16, textAlign:'center' }}>{set.p2}</span>
                            {i < sets.length-1 && <span style={{ margin:'0 4px', color:'var(--gray-200)', fontSize:10 }}>|</span>}
                          </React.Fragment>
                        );
                      })
                    : <span style={{ fontSize:12, fontWeight:700, color:'var(--gray-300)', padding:'0 8px' }}>vs</span>
                  }
                </div>
                <div style={{ flex:1, display:'flex', alignItems:'center', gap:8, justifyContent:'flex-end', minWidth:0 }}>
                  <span style={{ fontSize:13, fontWeight: String(t2?._id)===String(m?.winner)?700:500, color: String(t2?._id)===String(m?.winner)?'var(--navy)':'var(--gray-500)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textAlign:'right' }}>{t2?.teamName||'TBD'}</span>
                  <div style={{ width:28, height:28, borderRadius:6, background:'var(--navy)', color:'var(--lime)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, fontFamily:'var(--font-display)', flexShrink:0 }}>{t2?.teamCode||'?'}</div>
                </div>
              </div>
              {m?.specialResult && <div style={{ padding:'4px 12px', fontSize:10, color:'var(--gray-400)', borderTop:'1px solid var(--card-border)', textTransform:'capitalize' }}>{m.specialResult}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* Group selector — "All Groups" first, then individual */}
      <div className="pill-tabs" style={{ marginBottom:14 }}>
        <button
          className={`pill-tab ${activeGroup === 'all' ? 'pill-tab-active' : ''}`}
          onClick={() => setActiveGroup('all')}
        >
          All Groups
        </button>
        {groups.map(g => {
          const gFix = groupStageData.fixturesMap?.[g._id] || [];
          const done = gFix.filter(f => f.matchId?.status === 'completed').length;
          return (
            <button key={g._id}
              className={`pill-tab ${activeGroup === g._id ? 'pill-tab-active' : ''}`}
              onClick={() => setActiveGroup(g._id)}
            >
              {g.name}
              {gFix.length > 0 && <span style={{ marginLeft:4, fontSize:10, opacity:.7 }}>{done}/{gFix.length}</span>}
            </button>
          );
        })}
      </div>

      {/* Sub-tabs: Standings / Fixtures */}
      <div className="pill-tabs" style={{ marginBottom:16 }}>
        {[{k:'standings',l:'Standings'},{k:'fixtures',l:'Fixtures'}].map(t => (
          <button key={t.k}
            className={`pill-tab ${activeTab === t.k ? 'pill-tab-active' : ''}`}
            onClick={() => setActiveTab(t.k)}
          >{t.l}</button>
        ))}
      </div>

      {/* ── ALL GROUPS combined view ── */}
      {activeGroup === 'all' && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          {groups.map(g => (
            <div key={g._id} style={{ border:'1px solid var(--card-border)', borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'10px 14px', background:'var(--navy)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontWeight:700, fontSize:13, color:'var(--lime)', fontFamily:'var(--font-display)' }}>{g.name}</span>
                {(() => {
                  const gFix = groupStageData.fixturesMap?.[g._id] || [];
                  const done = gFix.filter(f => f.matchId?.status === 'completed').length;
                  return gFix.length > 0
                    ? <span style={{ fontSize:11, color:'rgba(255,255,255,.6)' }}>{done}/{gFix.length} played</span>
                    : null;
                })()}
              </div>
              <div style={{ padding:'12px' }}>
                {activeTab === 'standings' ? <StandingsTable gid={g._id} /> : <FixturesList gid={g._id} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SINGLE GROUP view ── */}
      {activeGroup !== 'all' && (
        <div>
          {activeTab === 'standings' ? <StandingsTable gid={activeGroup} /> : <FixturesList gid={activeGroup} />}
        </div>
      )}
    </div>
  );
}


export function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, joinTournament, leaveTournament, getRoundRobinStandings, applyCoupon, useCoupon, addDiscussionMessage, isAdvanced } = useApp();
  const [tab, setTab] = useState(null);
  const [matchFilter, setMatchFilter] = useState('all');
  const [matchPlayerSearch, setMatchPlayerSearch] = useState('');
  const [playersSearch, setPlayersSearch] = useState('');
  const [expandedRounds, setExpandedRounds] = useState({});
  const [ready, setReady] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [groupStageData, setGroupStageData] = useState(null); // null = no group stage

  useEffect(() => {
    const load = async () => {
      try {
        const res = await tournamentAPI.getDetails(id);
        const tournamentData = res.data.tournament;
        
        // If current user is organizer of this tournament, redirect to management page
        if (currentUser?.role === 'organizer' && String(currentUser?.id) === String(tournamentData.organizerId)) {
          navigate(`/organizer/tournament/${id}`, { replace: true });
          return;
        }
        
        setTournament(tournamentData);
        setMatches(res.data.matches);
        setPlayers(res.data.players);

        // Check for group stage
        try {
          const { groupStageAPI } = await import('../../services/groupStageAPI');
          const gsRes = await groupStageAPI.getGroupStageForTournament(id);
          if (gsRes?.data) {
            // Load full group data: teams, fixtures, standings per group
            const gs = gsRes.data;
            const groups = gs.groups || [];
            const [teamsResults, fixturesResults, standingsResults] = await Promise.all([
              Promise.all(groups.map(g =>
                groupStageAPI.getGroupTeams(g._id)
                  .then(r => ({ gid: g._id, teams: r.data || [] }))
                  .catch(() => ({ gid: g._id, teams: [] }))
              )),
              Promise.all(groups.map(g =>
                groupStageAPI.getGroupFixtures(g._id)
                  .then(r => ({ gid: g._id, fixtures: r.data || [] }))
                  .catch(() => ({ gid: g._id, fixtures: [] }))
              )),
              Promise.all(groups.map(g =>
                groupStageAPI.getGroupStandings(g._id)
                  .then(r => ({ gid: g._id, rows: r.data || [] }))
                  .catch(() => ({ gid: g._id, rows: [] }))
              )),
            ]);
            const tm = {}, fx = {}, st = {};
            teamsResults.forEach(({ gid, teams }) => { tm[gid] = teams; });
            fixturesResults.forEach(({ gid, fixtures }) => { fx[gid] = fixtures; });
            standingsResults.forEach(({ gid, rows }) => { st[gid] = rows; });
            setGroupStageData({ ...gs, teamsMap: tm, fixturesMap: fx, standingsMap: st });
            setTab('groups'); // default to groups tab for group stage tournaments
          }
        } catch (_) { /* not a group stage tournament */ }

        setReady(true);
      } catch (err) {
        console.error(err);
        setReady(true);
      }
    };
    load();
  }, [id, currentUser, navigate]);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [appliedCouponId, setAppliedCouponId] = useState(null);
  const [paid, setPaid] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [regeneratingSummary, setRegeneratingSummary] = useState(false);
  const [chatMsg, setChatMsg] = useState('');

  const [standings, setStandings] = useState([]);

  useEffect(() => {
    if (currentUser && id) {
      notificationAPI.getFollowed().then(res => {
        setFollowing((res.data?.tournaments || []).some(tid => String(tid) === String(id)));
      }).catch(() => {});
    }
  }, [currentUser, id]);

  const toggleFollow = async () => {
    if (!currentUser) return;
    setFollowLoading(true);
    try {
      if (following) {
        await notificationAPI.unfollow(id);
      } else {
        // Register push subscription on first follow
        if ('serviceWorker' in navigator && 'PushManager' in window && Notification.permission !== 'denied') {
          try {
            if (Notification.permission === 'default') await Notification.requestPermission();
            if (Notification.permission === 'granted') {
              const reg = await navigator.serviceWorker.ready;
              const { data: { publicKey } } = await notificationAPI.getVapidKey();
              if (publicKey) {
                let sub = await reg.pushManager.getSubscription();
                if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: publicKey });
                await notificationAPI.subscribe(sub.toJSON());
              }
            }
          } catch {}
        }
        await notificationAPI.follow(id);
      }
      setFollowing(!following);
    } catch {}
    setFollowLoading(false);
  };

  const t = tournament;
  const isKO = t ? t.type === 'Knockout' : true;

  const tMatches = matches;
  const isDoubles = t ? t.matchType === 'Doubles' : false;
  const isPlayer = currentUser?.role === 'player';
  const myPid = currentUser?.playerId;
  const isJoined = myPid && t && isPlayerRegistered(myPid, t.registeredPlayers);
  const canJoin = isPlayer && !isJoined && t?.status === 'upcoming' && t && t.registeredPlayers.length < t.maxPlayers;
  const canLeave = isPlayer && isJoined && t?.status === 'upcoming';

  const isOrganizer = currentUser?.id === t?.organizerId;
  const canChat = isJoined || isOrganizer;

  const hasInfo = t?.rules || t?.venueMap || t?.parking || t?.contactPhone || t?.contactEmail || (t?.announcements || []).length > 0;
  const hasSummary = t?.status === 'completed' && (t?.aiSummary || (isOrganizer && isAdvanced));
  const defaultTab = hasSummary ? 'summary' : t?.bracketGenerated ? (isKO ? 'bracket' : 'standings') : 'players';
  const activeTab = tab || defaultTab;

  useEffect(() => {
    if (t && !isKO && t.id) {
      getRoundRobinStandings(t.id).then(d => setStandings(d || []));
    }
  }, [t?.id, isKO, getRoundRobinStandings]);

  // Auto-expand all rounds when matches tab is active
  useEffect(() => {
    if (matches.length > 0 && activeTab === 'matches') {
      const rounds = [...new Set(matches.map(m => m.round))];
      const initialExpanded = {};
      rounds.forEach(round => { initialExpanded[round] = true; });
      setExpandedRounds(prev => {
        const hasChanges = rounds.some(r => !prev[r]);
        return hasChanges ? initialExpanded : prev;
      });
    }
  }, [matches.length, matchFilter, matchPlayerSearch, activeTab]);

  if (!ready) return <TournamentDetailSkeleton />;
  if (!t) return <div style={s.container}><h2>Tournament not found</h2></div>;

  const baseTabs = isKO
    ? [
        ...(hasSummary ? [{ key: 'summary', label: 'Summary' }] : []),
        ...(groupStageData ? [{ key: 'groups', label: 'Groups' }] : []),
        ...(t.bracketGenerated ? [{ key: 'bracket', label: 'Draw' }] : []),
        ...(t.bracketGenerated ? [{ key: 'matches', label: 'Results' }] : []),
        ...(!groupStageData ? [{ key: 'players', label: 'Players' }] : [])
      ]
    : [
        ...(hasSummary ? [{ key: 'summary', label: 'Summary' }] : []),
        ...(groupStageData ? [{ key: 'groups', label: 'Groups' }] : []),
        ...(t.bracketGenerated ? [{ key: 'standings', label: 'Standings' }] : []),
        ...(t.bracketGenerated ? [{ key: 'matches', label: 'Results' }] : []),
        ...(!groupStageData ? [{ key: 'players', label: 'Players' }] : [])
      ];
  const tabs = [...baseTabs, { key: 'discussion', label: 'Discussion' }, ...(hasInfo ? [{ key: 'info', label: 'Info' }] : [])];

  const openPayment = () => {
    const fee = t.entryFee || 0;
    setPayAmount(fee);
    setCouponCode('');
    setCouponResult(null);
    setAppliedCouponId(null);
    setPaid(false);
    setPayModal(true);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    const result = await applyCoupon(t.id, couponCode, t.entryFee || 0);
    setCouponResult(result);
    if (result.valid) {
      setPayAmount(result.finalAmount);
      setAppliedCouponId(result.couponId);
    }
  };

  const reloadDetail = async () => {
    try {
      const res = await tournamentAPI.getDetails(id);
      setTournament(res.data.tournament);
      setMatches(res.data.matches);
      setPlayers(res.data.players);
    } catch (e) { console.error(e); }
  };

  const handleConfirmPayment = async () => {
    if (appliedCouponId) await useCoupon(t.id, appliedCouponId);
    await joinTournament(t.id, myPid, isDoubles ? partnerId : null);
    setPaid(true);
    await reloadDetail();
  };

  const upiLink = t.upiId ? `upi://pay?pa=${t.upiId}&pn=TennisBuzz&am=${payAmount}&cu=INR&tn=Entry-${t.name}` : '';
  const qrUrl = payAmount > 0 && t.upiId ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiLink)}` : '';

  const surfaceColors = { 'Hard Court': '#1E88E5', 'Clay': '#ffb68f', 'Grass': '#43A047' };
  const surfaceColor = surfaceColors[t.surface] || '#8892A6';
  const fillPct = (t.registeredPlayers.length / t.maxPlayers) * 100;

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#00205B', fontWeight: 600, fontSize: 14 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <StatusBadge status={t.status} />
          {currentUser && <button onClick={toggleFollow} disabled={followLoading} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: following ? 'rgba(189,255,0,0.15)' : 'var(--gray-50)', color: following ? '#BDFF00' : 'var(--gray-500)', border: `1px solid ${following ? 'rgba(189,255,0,0.4)' : 'var(--gray-200)'}`, borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s', padding: 0 }} title={following ? 'Following' : 'Follow'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill={following ? '#BDFF00' : 'none'} stroke={following ? '#BDFF00' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          </button>}
          <ShareButton text={`Check out ${t.name} on TennisBuzz!`} url={window.location.href} compact />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <h1 className="heading" style={{ margin: 0, fontSize: 18, wordBreak: 'break-word' }}>{t.name}</h1>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--gray-400)', flexWrap: 'wrap' }}>
              <span><Ico d={Icons.calendar} size={10} color="var(--gray-400)" /> {new Date(t.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              <span>{t.registeredPlayers.length}/{t.maxPlayers} {isDoubles ? 'teams' : 'players'}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {canJoin && <button onClick={openPayment} style={{ padding: '8px 16px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" }}>Join Tournament</button>}
          {canLeave && <button onClick={async () => { await leaveTournament(t.id, myPid); await reloadDetail(); }} style={{ padding: '8px 16px', background: 'var(--gray-50)', color: 'var(--gray-600)', border: '1.5px solid var(--gray-200)', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>Leave</button>}
          {!currentUser && t.status === 'upcoming' && <Link to="/login" style={{ padding: '8px 16px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: 12, letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" }}>Sign In to Join</Link>}
          {isPlayer && isJoined && t.status !== 'upcoming' && <span style={{ fontSize: 11, color: '#43A047', fontWeight: 700, background: '#E8F5E9', padding: '4px 10px', borderRadius: 6 }}>✓ Registered</span>}
        </div>
      </div>

      <div className="tabs">
        {tabs.map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)} className={`tab ${activeTab === tb.key ? 'tab-active' : ''}`}>
            {tb.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16, animation: 'fadeIn 0.3s ease' }}>
        {activeTab === 'bracket' && isKO && (t.bracketGenerated
          ? <KnockoutBracket matches={tMatches} players={players} seeds={t.seeds || []} isDoubles={isDoubles} tournamentName={t.name} />
          : <Card style={{ textAlign: 'center', padding: 48 }}><h3 style={{ color: '#00205B' }}>Draw Not Available</h3><p style={{ color: '#8892A6', fontSize: 13, marginTop: 4 }}>The draw will be published once registrations close.</p></Card>
        )}
        {activeTab === 'standings' && !isKO && (t.bracketGenerated
          ? <RoundRobinTable standings={standings} players={players} />
          : <Card style={{ textAlign: 'center', padding: 48 }}><h3 style={{ color: '#00205B' }}>Fixtures Pending</h3><p style={{ color: '#8892A6', fontSize: 13, marginTop: 4 }}>Fixtures will be generated by the organizer.</p></Card>
        )}
        {activeTab === 'summary' && hasSummary && (
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', boxShadow: '0 2px 8px rgba(0,32,91,0.04)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', background: 'linear-gradient(135deg, rgba(0,32,91,0.04), rgba(30,136,229,0.04))' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #00205B, #1E88E5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ico d={Icons.trophy} size={14} color="#BDFF00" />
                  </div>
                  <span className="heading" style={{ fontSize: 10, letterSpacing: 1.5, margin: 0 }}>TOURNAMENT SUMMARY</span>
                </div>
                {isOrganizer && isAdvanced && (
                  <button
                    onClick={async () => {
                      setRegeneratingSummary(true);
                      try {
                        const res = await tournamentAPI.regenerateTournamentSummary(id);
                        if (res.data) setTournament(res.data);
                      } catch (e) { console.error(e); }
                      setRegeneratingSummary(false);
                    }}
                    disabled={regeneratingSummary}
                    style={{ padding: '5px 12px', fontSize: 10, fontWeight: 700, background: regeneratingSummary ? 'var(--gray-200)' : '#00205B', color: regeneratingSummary ? 'var(--gray-400)' : '#BDFF00', border: 'none', borderRadius: 6, cursor: regeneratingSummary ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-display)', letterSpacing: 0.5 }}
                  >
                    {regeneratingSummary ? '⏳ Generating...' : t.aiSummary ? '↻ Regenerate' : '✦ Generate'}
                  </button>
                )}
              </div>
            </div>
            <div style={{ padding: '14px 16px' }}>
              {t.aiSummary ? (
                <div style={{ fontSize: 13, color: '#3D4759', lineHeight: 1.75 }}>
                  {(() => {
                    const renderText = (text) => {
                      const parts = text.split(/\*\*(.*?)\*\*/g);
                      return parts.map((part, j) => j % 2 === 1
                        ? <strong key={j} style={{ color: '#00205B' }}>{part}</strong>
                        : <span key={j}>{part}</span>
                      );
                    };
                    const iconMap = { '🏆': { icon: Icons.trophy, color: '#FB8C00' }, '📊': { icon: Icons.target, color: '#1E88E5' }, '🔥': { icon: Icons.racket, color: '#E53935' }, '⚔': { icon: Icons.tennisBall, color: '#7E57C2' }, '⭐': { icon: Icons.medal, color: '#43A047' } };
                    const sections = [];
                    let current = null;
                    t.aiSummary.split('\n').forEach(line => {
                      const trimmed = line.trim();
                      if (!trimmed) return;
                      const headerMatch = trimmed.match(/^([\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2694}\u{2B50}])\uFE0F?\s*(.+)/u);
                      if (headerMatch && /[A-Z]{2,}/.test(headerMatch[2])) {
                        current = { emoji: headerMatch[1], title: headerMatch[2].replace(/^\**\s*/, '').replace(/\**\s*:?$/, '').trim(), lines: [] };
                        sections.push(current);
                      } else if (current) {
                        current.lines.push(trimmed);
                      } else {
                        sections.push({ emoji: null, title: null, lines: [trimmed] });
                      }
                    });
                    if (sections.length <= 1 && !sections[0]?.emoji) {
                      return <p style={{ margin: 0 }}>{renderText(t.aiSummary)}</p>;
                    }
                    return sections.map((sec, i) => {
                      if (!sec.emoji) return sec.lines.map((l, j) => <p key={`${i}-${j}`} style={{ margin: '6px 0' }}>{renderText(l)}</p>);
                      const mapped = iconMap[sec.emoji] || { icon: Icons.target, color: '#5F6B80' };
                      return (
                        <div key={i} style={{ marginBottom: i < sections.length - 1 ? 16 : 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 22, height: 22, borderRadius: 6, background: `${mapped.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Ico d={mapped.icon} size={12} color={mapped.color} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#00205B', letterSpacing: 1, fontFamily: 'var(--font-display)' }}>{sec.title}</span>
                          </div>
                          {sec.lines.map((l, j) => {
                            if (l.startsWith('- ') || l.startsWith('• ')) return (
                              <div key={j} style={{ display: 'flex', gap: 8, paddingLeft: 30, marginTop: 4 }}>
                                <span style={{ color: mapped.color, fontWeight: 700 }}>•</span>
                                <span>{renderText(l.slice(2))}</span>
                              </div>
                            );
                            return <p key={j} style={{ margin: '2px 0', paddingLeft: 30 }}>{renderText(l)}</p>;
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : isOrganizer && isAdvanced ? (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--gray-300)' }}>
                    {regeneratingSummary ? 'Generating summary...' : 'Click "Generate" to create an AI summary for this tournament.'}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        )}
        {activeTab === 'matches' && (() => {
          // Filter out bye matches
          const nonByeMatches = tMatches.filter(m => m.player1 && m.player2);
          
          const liveM = nonByeMatches.filter(m => m.status === 'live');
          const upcomingM = nonByeMatches.filter(m => m.status === 'scheduled' || m.status === 'pending').sort((a, b) => {
            const getDateTime = (match) => {
              if (!match.date) return new Date('9999-12-31').getTime();
              const dateTimeStr = match.date + 'T' + (match.time || '00:00');
              return new Date(dateTimeStr).getTime();
            };
            return getDateTime(a) - getDateTime(b);
          });
          const completedM = nonByeMatches.filter(m => m.status === 'completed').sort((a, b) => {
            const getDateTime = (match) => {
              if (!match.date) return 0;
              const dateTimeStr = match.date + 'T' + (match.time || '00:00');
              return new Date(dateTimeStr).getTime();
            };
            return getDateTime(b) - getDateTime(a);
          });
          
          const sortedAllMatches = [
            ...liveM,
            ...upcomingM,
            ...completedM
          ];
          
          // Apply player search filter
          const searchLower = matchPlayerSearch.toLowerCase();
          const matchesPlayerSearch = (m) => {
            if (!searchLower) return true;
            const t1 = m.player1 ? resolveTeam(m.player1, players) : { name: '' };
            const t2 = m.player2 ? resolveTeam(m.player2, players) : { name: '' };
            return t1.name.toLowerCase().includes(searchLower) || t2.name.toLowerCase().includes(searchLower);
          };
          
          const filteredByStatus = matchFilter === 'live' ? liveM : matchFilter === 'upcoming' ? upcomingM : matchFilter === 'completed' ? completedM : sortedAllMatches;
          const visibleMatches = filteredByStatus.filter(matchesPlayerSearch);

          const subTabs = [
            { key: 'all', label: 'All', count: sortedAllMatches.filter(matchesPlayerSearch).length },
            { key: 'live', label: 'Live', count: liveM.filter(matchesPlayerSearch).length, dot: true },
            { key: 'upcoming', label: 'Upcoming', count: upcomingM.filter(matchesPlayerSearch).length },
            { key: 'completed', label: 'Completed', count: completedM.filter(matchesPlayerSearch).length },
          ];

          if (sortedAllMatches.length === 0)
            return <Card><p style={{ color: '#8892A6', textAlign: 'center', padding: '24px 0' }}>No matches yet</p></Card>;

          return (
            <div>
              {/* Player Search */}
              <div style={{ marginBottom: 14, position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  className="form-input"
                  placeholder="Search by player name..."
                  value={matchPlayerSearch}
                  onChange={e => setMatchPlayerSearch(e.target.value)}
                  style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
                  onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
                  onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              
              {/* Sub-filter pills */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', flexWrap: 'nowrap' }} className="result-pills-scroll">
                {subTabs.map(st => (
                  <button key={st.key} onClick={() => setMatchFilter(st.key)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${matchFilter === st.key ? '#00205B' : '#EEF1F6'}`, background: matchFilter === st.key ? '#00205B' : 'var(--card-bg)', color: matchFilter === st.key ? '#BDFF00' : '#8892A6', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit', sans-serif", transition: 'all 0.2s', flexShrink: 0 }}>
                    {st.dot && st.count > 0 && <span style={{ width: 6, height: 6, borderRadius: '50%', background: matchFilter === st.key ? '#BDFF00' : '#E53935', animation: 'pulse 1.5s infinite' }} />}
                    {st.label}
                    <span style={{ fontSize: 10, fontWeight: 700, background: matchFilter === st.key ? 'rgba(189,255,0,0.2)' : '#EEF1F6', color: matchFilter === st.key ? '#BDFF00' : '#8892A6', padding: '1px 6px', borderRadius: 10 }}>{st.count}</span>
                  </button>
                ))}
              </div>

              {visibleMatches.length === 0 ? (
                <Card><p style={{ color: '#8892A6', textAlign: 'center', padding: '24px 0' }}>No {matchFilter} matches{matchPlayerSearch ? ' matching your search' : ''}</p></Card>
              ) : (() => {
                // Group by round
                const byRound = {};
                visibleMatches.forEach(m => {
                  const r = m.round || 'Other';
                  if (!byRound[r]) byRound[r] = [];
                  byRound[r].push(m);
                });

                const roundOrder = ['Round of 128', 'Round of 64', 'Round of 32', 'Round of 16', 'Quarter Final', 'Semi Final', 'Final'];
                const sortedRounds = Object.keys(byRound).sort((a, b) => {
                  const ai = roundOrder.indexOf(a), bi = roundOrder.indexOf(b);
                  if (ai !== -1 && bi !== -1) return ai - bi;
                  if (ai !== -1) return -1;
                  if (bi !== -1) return 1;
                  return a.localeCompare(b);
                });
                
                return (
                  <div style={{ position: 'relative' }}>
                    {/* Vertical timeline line */}
                    <div style={{ position: 'absolute', left: 9, top: 0, bottom: 0, width: 2, background: 'linear-gradient(180deg, #00205B 0%, #1E88E5 100%)', zIndex: 0 }} />
                    
                    {sortedRounds.map((round, idx) => {
                      const roundMatches = byRound[round];
                      const isExpanded = expandedRounds[round];
                      return (
                        <div key={round} style={{ position: 'relative', paddingLeft: 28, marginBottom: idx < sortedRounds.length - 1 ? 20 : 0 }}>
                          {/* Tennis ball node */}
                          <div
                            onClick={() => setExpandedRounds(prev => ({ ...prev, [round]: !prev[round] }))}
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: isExpanded ? '#00205B' : '#8892A6',
                              border: '2px solid #fff',
                              boxShadow: isExpanded ? '0 0 0 3px rgba(0,32,91,0.2)' : 'none',
                              cursor: 'pointer',
                              zIndex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke={isExpanded ? '#BDFF00' : '#fff'} strokeWidth="2" fill="none" />
                              <path d="M12 2c0 5.5-4.5 10-10 10" stroke={isExpanded ? '#BDFF00' : '#fff'} strokeWidth="1.5" fill="none" />
                              <path d="M12 22c0-5.5 4.5-10 10-10" stroke={isExpanded ? '#BDFF00' : '#fff'} strokeWidth="1.5" fill="none" />
                            </svg>
                          </div>
                          
                          {/* Round header */}
                          <div
                            onClick={() => setExpandedRounds(prev => ({ ...prev, [round]: !prev[round] }))}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: isExpanded ? 12 : 0,
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <h3 style={{ fontSize: 11, fontWeight: 800, color: '#00205B', letterSpacing: 1.5, textTransform: 'uppercase', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                              {round}
                            </h3>
                            <span style={{ fontSize: 10, fontWeight: 700, background: '#EEF1F6', color: '#8892A6', padding: '2px 8px', borderRadius: 10 }}>
                              {roundMatches.length}
                            </span>
                          </div>
                          
                          {/* Matches grid */}
                          {isExpanded && (
                            <div className="grid-matches" style={{ marginTop: 0 }}>
                              {roundMatches.map(m => {
                          const t1 = resolveTeam(m.player1, players);
                          const t2 = resolveTeam(m.player2, players);
                          const p1obj = players.find(p => p.id === m.player1);
                          const p2obj = players.find(p => p.id === m.player2);
                          const isLive = m.status === 'live';
                          const isDone = m.status === 'completed';
                          const w1 = isDone && m.winner === m.player1;
                          const w2 = isDone && m.winner === m.player2;
                          const sets = m.score?.sets || [];

                          const renderRow = (team, pobj, isWinner, isLoser, pKey, oKey) => {
                            // Check if doubles team
                            const isDoublesTeam = team.name.includes(' / ');
                            const players = isDoublesTeam ? team.name.split(' / ') : [team.name];
                            
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isWinner ? 'rgba(67,160,71,0.08)' : 'transparent', borderLeft: isWinner ? '3px solid #43A047' : '3px solid transparent' }}>
                                <PlayerAvatar name={team.name} photo={pobj?.photo} size={28} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {isDoublesTeam ? (
                                    <div>
                                      <div style={{ fontSize: 12, fontWeight: isWinner ? 700 : 500, color: isWinner ? '#43A047' : isLoser ? '#B0B8C9' : '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {players[0]}
                                      </div>
                                      <div style={{ fontSize: 11, fontWeight: isWinner ? 600 : 400, color: isWinner ? '#43A047' : isLoser ? '#D8DDE8' : '#5F6B80', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {players[1]}
                                      </div>
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: 12, fontWeight: isWinner ? 700 : 500, color: isWinner ? '#43A047' : isLoser ? '#B0B8C9' : '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {team.name}
                                    </span>
                                  )}
                                </div>
                                {isWinner && (
                                  <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#43A047', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5 }}>WON</span>
                                )}
                                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                                  {sets.map((sv, i) => {
                                    const mine = sv[pKey], opp = sv[oKey];
                                    const won = mine > opp;
                                    return (
                                      <span key={i} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: won ? 700 : 400, color: won ? '#43A047' : '#B0B8C9', background: won ? 'rgba(67,160,71,0.1)' : 'transparent', borderRadius: 5, fontFamily: "'Outfit', sans-serif" }}>{mine}</span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          };

                          return (
                            <Link to={`/matches/${m.id}`} key={m.id} style={{ textDecoration: 'none' }}>
                              <div style={{ background: '#FFFFFF', borderRadius: 12, border: `2px solid ${isLive ? '#E53935' : isDone ? '#43A047' : '#00205B'}`, overflow: 'hidden', boxShadow: isLive ? '0 4px 12px rgba(229,57,53,0.15)' : isDone ? '0 2px 8px rgba(67,160,71,0.08)' : '0 2px 8px rgba(0,32,91,0.1)', transition: 'all 0.2s', cursor: 'pointer' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 10px', borderBottom: '1px solid #F4F6FA', background: isLive ? 'rgba(229,57,53,0.05)' : isDone ? 'rgba(67,160,71,0.03)' : 'rgba(0,32,91,0.03)' }}>
                                  <span style={{ fontSize: 10, color: '#5F6B80', fontWeight: 600 }}>{m.round}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {isLive && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, color: '#E53935', letterSpacing: 0.8, background: '#FFEBEE', padding: '2px 6px', borderRadius: 3 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />LIVE</span>}
                                    {isDone && <span style={{ fontSize: 9, fontWeight: 800, color: '#43A047', letterSpacing: 0.5, background: '#E8F5E9', padding: '2px 6px', borderRadius: 3 }}>COMPLETED</span>}
                                    {!isLive && !isDone && <span style={{ fontSize: 9, fontWeight: 700, color: '#00205B', background: 'rgba(0,32,91,0.08)', padding: '2px 6px', borderRadius: 3 }}>UPCOMING</span>}
                                  </div>
                                </div>
                                <div style={{ padding: '3px 0 6px' }}>
                                  {renderRow(t1, p1obj, w1, w2, 'p1', 'p2')}
                                  <div style={{ height: 1, background: '#F4F6FA', margin: '0 14px' }} />
                                  {renderRow(t2, p2obj, w2, w1, 'p2', 'p1')}
                                </div>
                                {(m.court || m.date || m.time) && (
                                  <div style={{ padding: '6px 10px', borderTop: '1px solid #F4F6FA', background: '#F8F9FC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    {m.court && <span style={{ fontSize: 10, color: '#00205B', fontWeight: 700, background: '#fff', padding: '3px 8px', borderRadius: 4, border: '1px solid #D8DDE8' }}>{m.court}</span>}
                                    {(m.date || m.time) && (
                                      <span style={{ fontSize: 9, color: '#00205B', fontWeight: 700, background: '#fff', padding: '3px 8px', borderRadius: 4, border: '1px solid #EEF1F6', display: 'flex', alignItems: 'center', gap: 4, marginLeft: m.court ? 'auto' : 0 }}>
                                        <Ico d={Icons.calendar} size={10} color="#5F6B80" />
                                        {m.date && new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        {m.date && m.time && ' · '}
                                        {m.time}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* End ball icon */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      bottom: -10,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#43A047',
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 3px rgba(67,160,71,0.2)',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke="#fff" strokeWidth="2" fill="none" />
                        <path d="M12 2c0 5.5-4.5 10-10 10" stroke="#fff" strokeWidth="1.5" fill="none" />
                        <path d="M12 22c0-5.5 4.5-10 10-10" stroke="#fff" strokeWidth="1.5" fill="none" />
                      </svg>
                    </div>
                  </div>
              );
              })()}
            </div>
          );
        })()}
        {activeTab === 'groups' && groupStageData && (
          <PublicGroupView groupStageData={groupStageData} />
        )}
        {activeTab === 'players' && (
          <div>
            <div style={{ marginBottom: 14, position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="form-input"
                placeholder="Search players..."
                value={playersSearch}
                onChange={e => setPlayersSearch(e.target.value)}
                style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
                onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
                onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={s.pGrid}>
            {t.registeredPlayers
              .map(rp => {
                const team = resolveTeam(rp, players);
                const isSingle = rp && !rp.includes('+');
                const playerObj = isSingle ? players.find(p => p.id === rp) : null;
                return { rp, team, isSingle, playerObj };
              })
              .filter(({ team }) => {
                const searchLower = playersSearch.toLowerCase();
                return !searchLower || team.name.toLowerCase().includes(searchLower);
              })
              .map(({ rp, team, isSingle, playerObj }) => (
                <Card 
                  key={rp} 
                  style={{ 
                    padding: 14, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 12, 
                    cursor: isSingle ? 'pointer' : 'default' 
                  }}
                  onClick={() => isSingle && navigate(`/players/${rp}`)}
                >
                  <PlayerAvatar name={team.name} photo={playerObj?.photo} size={36} />
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: '#00205B', fontSize: 14 }}>{team.name}</strong>
                    {!isDoubles && playerObj && <div style={{ fontSize: 11, color: '#8892A6' }}>{playerObj.country}</div>}
                  </div>
                  {isSingle && <Ico d={Icons.chevron} size={14} color="#B0B8C9" />}
                </Card>
              ))}
            {t.registeredPlayers.filter(rp => {
              const team = resolveTeam(rp, players);
              const searchLower = playersSearch.toLowerCase();
              return !searchLower || team.name.toLowerCase().includes(searchLower);
            }).length === 0 && <Card><p style={{ color: '#8892A6', textAlign: 'center' }}>No players found</p></Card>}
          </div>
          </div>
        )}
        {activeTab === 'discussion' && (
          <div>
            {canChat ? (
              <form onSubmit={e => { e.preventDefault(); if (chatMsg.trim()) { addDiscussionMessage(id, chatMsg); setChatMsg(''); } }} style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input className="form-input" style={{ flex: 1 }} placeholder="Write a message..." value={chatMsg} onChange={e => setChatMsg(e.target.value)} />
                <button type="submit" disabled={!chatMsg.trim()} style={{ padding: '10px 18px', background: chatMsg.trim() ? '#00205B' : 'var(--gray-200)', color: chatMsg.trim() ? '#BDFF00' : 'var(--gray-400)', border: 'none', borderRadius: 8, fontWeight: 700, cursor: chatMsg.trim() ? 'pointer' : 'default', fontSize: 12, fontFamily: "var(--font-display)", flexShrink: 0 }}>Send</button>
              </form>
            ) : (
              <Card style={{ padding: 14, textAlign: 'center', marginBottom: 14 }}>
                <p style={{ color: '#8892A6', fontSize: 13, margin: 0 }}>{currentUser ? 'Only registered players can post' : <Link to="/login" style={{ color: '#1E88E5', textDecoration: 'none', fontWeight: 600 }}>Sign in to join the discussion</Link>}</p>
              </Card>
            )}
            {(t.discussion || []).length === 0 ? (
              <Card style={{ padding: '24px 16px', textAlign: 'center' }}>
                <p style={{ color: '#8892A6', fontSize: 13, margin: 0 }}>No messages yet. Start the conversation!</p>
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...(t.discussion || [])].reverse().map(msg => (
                  <Card key={msg.id} style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <PlayerAvatar name={msg.userName} size={22} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{msg.userName}</span>
                      {msg.role === 'organizer' && <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: '#00205B', padding: '2px 7px', borderRadius: 10, letterSpacing: 0.3 }}>Organizer</span>}
                      <span style={{ fontSize: 9, color: 'var(--gray-300)', marginLeft: 'auto' }}>{new Date(msg.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.5, paddingLeft: 28 }}>{msg.text}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 'info' && (
          <div>
            {(t.announcements || []).length > 0 && <>
              <h3 style={{ fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 10, fontWeight: 800, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.megaphone} size={14} /> ANNOUNCEMENTS</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {[...(t.announcements || [])].reverse().map(a => (
                  <Card key={a.id} style={{ padding: '12px 16px', borderLeft: '3px solid #1E88E5' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#00205B', marginBottom: 2 }}>{a.title}</div>
                    <p style={{ fontSize: 13, color: '#3D4759', margin: '0 0 4px', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{a.message}</p>
                    <span style={{ fontSize: 10, color: '#B0B8C9' }}>{new Date(a.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </Card>
                ))}
              </div>
            </>}
            {t.rules && <Card style={{ marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.clipboard} size={14} /> Tournament Rules</h3>
              <p style={{ fontSize: 13, color: '#3D4759', whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0 }}>{t.rules}</p>
            </Card>}
            <Card>
              <h3 style={{ margin: '0 0 10px', fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.pin} size={14} /> Venue & Contact</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {t.location && <div style={{ fontSize: 13, color: '#3D4759', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.pin} size={12} color="#5F6B80" /> {t.location}</div>}
                {t.venueMap && <a href={t.venueMap} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1E88E5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.map} size={12} /> Open Map / Directions</a>}
                {t.parking && <div style={{ fontSize: 13, color: '#3D4759', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.parking} size={12} color="#5F6B80" /> {t.parking}</div>}
                {t.contactPhone && <a href={`tel:${t.contactPhone}`} style={{ fontSize: 13, color: '#3D4759', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.phone} size={12} color="#5F6B80" /> {t.contactName ? `${t.contactName} · ` : ''}{t.contactPhone}</a>}
                {t.contactEmail && <a href={`mailto:${t.contactEmail}`} style={{ fontSize: 13, color: '#1E88E5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.mail} size={12} /> {t.contactEmail}</a>}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <Modal isOpen={payModal} onClose={() => setPayModal(false)} title="Join Tournament">
        {paid ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12, display: 'flex', justifyContent: 'center' }}><Ico d={Icons.check} size={48} color="#43A047" /></div>
            <h3 style={{ color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 4 }}>Registration Successful!</h3>
            <p style={{ color: '#8892A6', fontSize: 13 }}>You are now registered for {t.name}</p>
            <button onClick={() => setPayModal(false)} style={{ ...s.joinBtn, marginTop: 16, padding: '10px 32px' }}>Done</button>
          </div>
        ) : (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <h3 style={{ color: '#00205B', fontFamily: "'Outfit', sans-serif", margin: 0 }}>{t.name}</h3>
              <div style={{ fontSize: 12, color: '#8892A6', marginTop: 4 }}>Entry Fee: ₹{(t.entryFee || 0).toLocaleString('en-IN')}</div>
            </div>

            {/* Doubles: Partner Selection */}
            {isDoubles && (
              <div style={{ marginBottom: 16 }}>
                <label style={s.fLabel}>Select Your Partner</label>
                <select style={{ ...s.fInput, width: '100%' }} value={partnerId} onChange={e => setPartnerId(e.target.value)} required>
                  <option value="">Choose a partner...</option>
                  {players.filter(p => p.id !== myPid && !isPlayerRegistered(p.id, t.registeredPlayers)).map(p => (
                    <option key={p.id} value={p.id}>{p.name} (#{p.ranking})</option>
                  ))}
                </select>
                {!partnerId && <div style={{ fontSize: 11, color: '#FB8C00', marginTop: 4 }}>You must select a partner for doubles</div>}
              </div>
            )}

            {/* Coupon Section */}
            {(t.coupons || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={s.fLabel}>Have a coupon?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...s.fInput, flex: 1 }} placeholder="Enter coupon code" value={couponCode} onChange={e => setCouponCode(e.target.value)} />
                  <button type="button" onClick={handleApplyCoupon} style={{ ...s.joinBtn, padding: '8px 16px', fontSize: 11 }}>Apply</button>
                </div>
                {couponResult && (
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: couponResult.valid ? '#43A047' : '#E53935' }}>
                    {couponResult.msg}
                  </div>
                )}
              </div>
            )}

            {/* Amount Summary */}
            <div style={{ background: '#F8F9FC', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #EEF1F6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: '#5F6B80' }}>Entry Fee</span>
                <span style={{ color: '#00205B', fontWeight: 600 }}>₹{(t.entryFee || 0).toLocaleString('en-IN')}</span>
              </div>
              {couponResult?.valid && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: '#43A047' }}>Discount</span>
                  <span style={{ color: '#43A047', fontWeight: 600 }}>-₹{couponResult.discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div style={{ borderTop: '1px solid #D8DDE8', paddingTop: 8, marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
                <span style={{ fontWeight: 700, color: '#00205B' }}>Total</span>
                <span style={{ fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif" }}>₹{payAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* UPI QR Code */}
            {payAmount > 0 && t.upiId ? (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#00205B', marginBottom: 8 }}>Scan QR to Pay via UPI</div>
                <img src={qrUrl} alt="UPI QR Code" style={{ width: 180, height: 180, borderRadius: 8, border: '1px solid #EEF1F6' }} />
                <div style={{ fontSize: 11, color: '#8892A6', marginTop: 6 }}>UPI ID: <strong>{t.upiId}</strong></div>
                <div style={{ fontSize: 10, color: '#B0B8C9', marginTop: 2 }}>Pay ₹{payAmount.toLocaleString('en-IN')} and click confirm below</div>
              </div>
            ) : payAmount === 0 ? (
              <div style={{ textAlign: 'center', marginBottom: 16, padding: 12, background: '#E8F5E9', borderRadius: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#43A047', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.check} size={16} color="#43A047" /> Free Entry!</div>
                <div style={{ fontSize: 11, color: '#5F6B80', marginTop: 2 }}>No payment required</div>
              </div>
            ) : null}

            <button onClick={handleConfirmPayment} style={{ ...s.joinBtn, width: '100%', padding: 12, fontSize: 13, ...(isDoubles && !partnerId ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
              {payAmount > 0 ? 'I Have Paid — Confirm Registration' : 'Confirm Registration'}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}

const s = {
  // Detail page only — still used by style={s.xxx}
  infoBadge: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 10px' },
  joinBtnHero: { padding: '10px 24px', background: '#BDFF00', color: '#00205B', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'none', textAlign: 'center', whiteSpace: 'nowrap', letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" },
  leaveBtnHero: { padding: '10px 24px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  prizeBox: { flex: '1 1 100px', textAlign: 'center', padding: '14px 12px', borderRight: '1px solid #EEF1F6' },
  prizeLabel: { fontSize: 10, fontWeight: 700, color: '#8892A6', letterSpacing: 1, fontFamily: "'Outfit', sans-serif", marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' },
  prizeValue: { fontSize: 18, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif" },
  prizeRow: { display: 'flex', gap: 12, marginBottom: 12 },
  bar: { height: 4, background: '#EEF1F6', borderRadius: 2 },
  barFill: { height: '100%', borderRadius: 2, transition: 'width 0.3s' },
  joinBtn: { padding: '10px 24px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'none', textAlign: 'center', whiteSpace: 'nowrap', letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" },
  pGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 },
  pRank: { width: 36, height: 36, borderRadius: 8, background: '#F8F9FC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: '#00205B', fontFamily: "'Outfit', sans-serif", border: '1px solid #EEF1F6' },
  fLabel: { fontSize: 10, fontWeight: 600, color: '#5F6B80', marginBottom: 4, display: 'block', letterSpacing: 0.5, textTransform: 'uppercase' },
  fInput: { padding: '10px 12px', border: '1.5px solid #D8DDE8', borderRadius: 8, fontSize: 13, outline: 'none', background: '#F8F9FC', color: '#2A3244', boxSizing: 'border-box' },
};
